import type { FC } from 'react';
import Form from '@components/Form';
import { Show } from '@components/Show';
import type { BlockEditFormState } from '@ContractBuilder/modules/block-edit';
import clsx from 'clsx';

import { DrawerBlockName } from '../components/DrawerBlockName';
import { DrawerPanelActions } from '../components/DrawerPanelActions';
import { DrawerPanelDetails } from '../components/DrawerPanelDetails';
import { DrawerPanelPermissions } from '../components/DrawerPanelPermissions';
import { DrawerPanelSettings } from '../components/DrawerPanelSettings';
import type { BlockDetails } from '../hooks/useBlockDetails';
import type { BlockEditFormSetter, ToggleFormField } from '../types';
import type { PanelMode } from '../utils/get-panel-mode';

export interface BlockConfigurationPanelProps {
  canDeleteBlock: boolean;
  details: BlockDetails;
  formValues?: BlockEditFormState;
  isBlockType: boolean;
  isCreate: boolean;
  isSuperAdminUser: boolean;
  mode: PanelMode;
  onDelete: () => void;
  onNameChange: (name: string) => void;
  setFormValues: BlockEditFormSetter;
  toggleField: ToggleFormField;
}

export const BlockConfigurationPanel: FC<BlockConfigurationPanelProps> = ({
  canDeleteBlock,
  details,
  formValues,
  isBlockType,
  isCreate,
  isSuperAdminUser,
  mode,
  onDelete,
  onNameChange,
  setFormValues,
  toggleField,
}) => {
  const editingBlockId = formValues?.id;

  return (
    <div
      className={clsx(
        'flex h-full w-full flex-col overflow-x-hidden border-l bg-white p-5',
        mode.isLibraryBlockEdit && 'pt-0',
      )}
    >
      <Form key={editingBlockId}>
        <Show when={!mode.isLibraryBlockEdit}>
          <DrawerBlockName
            className="-mx-7"
            isBlockType={isBlockType}
            name={formValues?.name}
            onNameChange={onNameChange}
          />
        </Show>
        <Show when={!mode.isEndorsement}>
          <DrawerPanelDetails
            details={details}
            formValues={formValues}
            isBlockType={isBlockType}
            mode={mode}
            setFormValues={setFormValues}
          />
        </Show>
        <DrawerPanelSettings
          formValues={formValues}
          isBlockType={isBlockType}
          mode={mode}
          setFormValues={setFormValues}
          toggleField={toggleField}
        />
        <Show when={mode.isAdminView}>
          <DrawerPanelPermissions
            formValues={formValues}
            isBlockType={isBlockType}
            isSuperAdminUser={isSuperAdminUser}
            mode={mode}
            toggleField={toggleField}
          />
        </Show>
        <Show when={!isCreate && !mode.isLibraryBlockEdit}>
          <DrawerPanelActions canDeleteBlock={canDeleteBlock} isBlockType={isBlockType} onDelete={onDelete} />
        </Show>
      </Form>
    </div>
  );
};
